/**
 * useGameActivityPush.js
 *
 * 統一的遊戲活動推送 composable
 * 用於將遊戲配置創建為活動、生成短網址並推送到 HiTeach
 *
 * 支援的遊戲類型：
 * - Mole (打地鼠)
 * - Maze (迷宮)
 * - PvP (對戰)
 * - PvPTF (是非對戰)
 * - Snake (貪食蛇)
 */

import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import gameActivityAPI from '@/api/gameActivityAPI';
import mindmapAPI from '@/api/mindmapAPI';

/**
 * 遊戲活動推送 composable
 *
 * @param {Object} options - 配置選項
 * @param {string} options.gameName - 遊戲名稱 ('mole' | 'maze' | 'star' | 'tf' | 'snake')
 * @param {string} options.gameType - 遊戲類型 ('molegame' | 'mazegame' | 'pvpgame' | 'pvptfgame' | 'snakegame')
 * @param {string} options.gameRoute - 遊戲路由 ('/ht-molegame-play' | '/ht-mazegame-play' | ...)
 * @param {string} options.questionType - 題目類型 ('recognition' | 'multiChoice' | 'trueFalse')
 * @param {Object} options.messageBridge - WebView 訊息橋接物件
 * @param {Ref} options.htVersion - HiTeach 版本號 ref
 * @param {Function} options.serializeGameConfig - 遊戲配置序列化函式 (config) => JSON string
 * @param {string} [options.activityNamePrefix] - 活動名稱前綴（選填，預設使用 gameConfig.topic）
 *
 * @returns {Object} - 包含 createAndPushShortUrl 等函式
 */
export const useGameActivityPush = (options) => {
    const { t, locale } = useI18n();
    const iviewMessage = inject('$Message');

    // 驗證必要參數
    if (!options.gameName || !options.gameType || !options.gameRoute || !options.questionType) {
        throw new Error('[useGameActivityPush] 缺少必要參數：gameName, gameType, gameRoute, questionType');
    }

    if (!options.messageBridge) {
        throw new Error('[useGameActivityPush] 缺少必要參數：messageBridge');
    }

    if (!options.htVersion) {
        throw new Error('[useGameActivityPush] 缺少必要參數：htVersion');
    }

    // 預設序列化函式：直接 JSON.stringify
    const serializeGameConfig = options.serializeGameConfig || ((config) => JSON.stringify(config));

    /**
     * 短網址域名（根據語言環境決定）
     */
    const shortURLHost = computed(() => {
        return locale.value?.toLowerCase() === 'zh-cn' ? 'hiteach.cn' : 'hiteach.cc';
    });

    /**
     * 檢查是否已登入
     */
    const isLoggedIn = computed(() => {
        const profileStr = localStorage.getItem('profile');
        if (!profileStr) return false;

        try {
            const profile = JSON.parse(profileStr);
            return !!(profile && profile.id);
        } catch {
            return false;
        }
    });

    /**
     * 從當前遊戲配置創建遊戲活動
     *
     * @param {Object} gameConfig - 遊戲配置物件
     * @param {number} playerCount - 玩家數量
     * @returns {Promise<Object>} - 創建的活動資料（包含 id）
     */
    const createGameActivityFromCurrentConfig = async (gameConfig, playerCount = 1, skipPush = false) => {
        try {
            // 從 localStorage 讀取 profile（由 setupUserAuth 儲存）
            const profileStr = localStorage.getItem('profile');
            if (!profileStr) {
                throw new Error('使用者資料不存在：找不到 profile');
            }

            const profile = JSON.parse(profileStr);
            if (!profile.id) {
                throw new Error('使用者資料不完整：profile 中沒有 id');
            }

            // 序列化遊戲配置
            const gameContentSettings = serializeGameConfig(gameConfig);

            // 活動名稱：優先使用 activityNamePrefix，否則使用 gameConfig.topic
            const activityName = options.activityNamePrefix || gameConfig.topic || `${options.gameType}遊戲`;

            // 🆕 處理 classRosterSource 和 classRoster
            let classRosterSource = 'none';
            let classRoster = JSON.stringify({ students: [] });

            // useHiTeachRoster 未設定（如 demo config）時，以 school_vers 作 fallback
            const shouldUseRoster = gameConfig.useHiTeachRoster != null
                ? !!gameConfig.useHiTeachRoster
                : localStorage.getItem('school_vers') === 'true';

            console.log(`[${options.gameName}] 名單診斷:`, {
                'gameConfig.useHiTeachRoster': gameConfig.useHiTeachRoster,
                shouldUseRoster,
                school_vers: localStorage.getItem('school_vers'),
                ht_memberList: sessionStorage.getItem('ht_memberList') ? `有資料(${JSON.parse(sessionStorage.getItem('ht_memberList'))?.length}筆)` : '無資料'
            });

            // 如果遊戲配置中標記使用 HiTeach 名單（未開始課堂時跳過名單處理與警告）
            if (shouldUseRoster && !skipPush) {
                const memberListStr = sessionStorage.getItem('ht_memberList');

                if (memberListStr) {
                    try {
                        const memberList = JSON.parse(memberListStr);

                        // 檢查名單是否有效（不為空陣列）
                        if (memberList && Array.isArray(memberList) && memberList.length > 0) {
                            classRosterSource = 'HT';
                            classRoster = memberListStr; // 直接使用 JSON string
                            console.log(`[${options.gameName}] 將使用 HiTeach 名單 (${memberList.length} 位學生)`);
                        } else {
                            // 名單為空陣列
                            console.warn(`[${options.gameName}] HiTeach 名單為空，改用匿名模式`);
                            iviewMessage.warning({
                                content: t('HTwbActKitHome.rosterEmptyWarning'),
                                duration: 4,
                                closable: true
                            });
                        }
                    } catch (parseError) {
                        console.error(`[${options.gameName}] 解析 memberList 失敗:`, parseError);
                        iviewMessage.warning({
                            content: t('HTwbActKitHome.rosterEmptyWarning'),
                            duration: 4,
                            closable: true
                        });
                    }
                } else {
                    // sessionStorage 中沒有名單，向 HiTeach 索取最新 Init.GameHome
                    console.warn(`[${options.gameName}] 已勾選使用 HiTeach 名單，但 sessionStorage 中無名單資料，發送 Get.GameInitInfo 索取`);
                    options.messageBridge.postMessage({ action: 'Get.GameInitInfo' });
                    iviewMessage.warning({
                        content: t('HTwbActKitHome.rosterEmptyWarning'),
                        duration: 4,
                        closable: true
                    });
                }
            }

            // 使用與 CreateActivityModal 相同的欄位名稱和結構
            const payload = {
                activityName: activityName,
                creator: {
                    id: profile.id,
                    name: profile.name || '使用者'
                },
                currentGame: options.gameName,
                questionType: options.questionType,
                gameContentSettings: gameContentSettings,
                classRosterSource: classRosterSource,  // 🆕 'none' 或 'HT'
                classRoster: classRoster,               // 🆕 JSON string
                shortUrlCode: '',
                enableEntry: true, // 推送後直接開放進入
                playerCount: playerCount,
                allowTemplateChange: false
            };

            console.log(`[${options.gameName}] 創建遊戲活動 payload:`, payload);
            const response = await gameActivityAPI.createGameActivity(payload);
            console.log(`[${options.gameName}] API 回應完整資料:`, response);

            // API 可能直接回傳物件，或在 data 裡面
            const activityData = response.data || response;
            console.log(`[${options.gameName}] 活動資料:`, activityData);

            if (!activityData || !activityData.id) {
                throw new Error('API 回傳資料格式錯誤：缺少 id 欄位');
            }

            return activityData; // 返回 GameActivity 物件（包含 id）
        } catch (error) {
            console.error(`[${options.gameName}] 創建遊戲活動失敗:`, error);
            throw error;
        }
    };

    /**
     * 生成短網址
     *
     * @param {string} activityId - 活動 ID
     * @returns {Promise<string>} - 短網址碼（例如：abc123）
     */
    const generateShortUrl = async (activityId) => {
        try {
            const fullUrl = `${window.location.origin}${options.gameRoute}?actId=${activityId}`;
            const dataForClip = {
                "origDataList": [{ "origData": fullUrl }],
                "type": "url",
                "useShortDigits": false,
                "isPermanent": false,
                "lang": locale.value.toLowerCase(),
                "note": JSON.stringify({ activityType: options.gameType, actId: activityId })
            };
            const clipRes = await mindmapAPI.getClipCode(dataForClip);
            const clipCode = clipRes?.clipCodePairs?.[0]?.clipCode;
            console.log(`[${options.gameName}] 短網址生成成功:`, clipCode);

            // 回傳原始 URL 與短碼，方便後續同時推送兩種連結
            return {
                originalUrl: fullUrl,
                shortUrlCode: clipCode
            };
        } catch (error) {
            console.error(`[${options.gameName}] 生成短網址失敗:`, error);
            throw error;
        }
    };

    /**
     * 更新 GameActivity 的短網址
     *
     * @param {string} activityId - 活動 ID
     * @param {string} shortUrlCode - 短網址碼
     * @returns {Promise<Object>} - 更新後的活動資料
     */
    const updateActivityWithShortUrl = async (activityId, shortUrlCode) => {
        try {
            const payload = {
                id: activityId,
                shortUrlCode: shortUrlCode,
                enableEntry: true
            };

            const response = await gameActivityAPI.updateGameActivity(payload);
            console.log(`[${options.gameName}] 更新遊戲活動短網址成功:`, response.data);
            return response.data;
        } catch (error) {
            console.error(`[${options.gameName}] 更新遊戲活動短網址失敗:`, error);
            throw error;
        }
    };

    /**
     * 創建活動並推送短網址（新版本 HiTeach >= 5.5.0.0002）
     *
     * @param {Object} gameConfig - 遊戲配置物件
     * @param {number} playerCount - 玩家數量
     * @returns {Promise<Object>} - 返回 { success: boolean, activityId?: string, shortUrl?: string, error?: string }
     */
    const createAndPushShortUrl = async (gameConfig, playerCount = 1, { skipPush = false } = {}) => {
        try {
            // 1. 創建遊戲活動
            const activity = await createGameActivityFromCurrentConfig(gameConfig, playerCount, skipPush);

            // 2. 生成短網址（取得原始 URL 與短碼）
            const { originalUrl, shortUrlCode } = await generateShortUrl(activity.id);

            // 3. 更新活動的短網址
            await updateActivityWithShortUrl(activity.id, shortUrlCode);

            // 4. 構建完整短網址
            const shortUrl = `https://${shortURLHost.value}/${shortUrlCode}`;

            // 5. 推送連結到 HiTeach（若目前未開始課堂則跳過推送，僅上傳雲端）
            if (!skipPush) {
                options.messageBridge.postMessage({
                    action: 'SendGameLink.WebIRS',
                    data: {
                        url: originalUrl,
                        shortUrl: shortUrl,
                        activityId: activity.id,
                        gameType: options.gameType  // 遊戲類型：molegame, pvpgame, pvptfgame（用於翻頁恢復時選擇正確路由）
                     }
                });
                console.log(`[${options.gameName}] 推送短網址成功:`, shortUrl);
            } else {
                console.log(`[${options.gameName}] 跳過推送（未開始課堂），僅上傳雲端:`, shortUrl);
            }

            // 成功提示（未開始課堂時使用不同訊息）
            iviewMessage.success({
                content: skipPush ? t('Game.uploadSuccess') : t('Game.pushSuccess'),
                duration: 3,
                closable: true
            });

            // ✨ 返回成功結果，包含 activityId 供查看成績功能使用
            return {
                success: true,
                activityId: activity.id,
                shortUrl: shortUrl
            };
        } catch (error) {
            console.error(`[${options.gameName}] 創建並推送短網址失敗:`, error);

            // 檢查是否為活動數量超限錯誤
            const isLimitExceeded = error.response?.data?.errorCode === 'ACTIVITY_LIMIT_EXCEEDED' ||
                                   error.response?.data?.message?.includes('已達活動數量上限') ||
                                   error.response?.data?.message?.includes('已达活动数量上限');

            if (isLimitExceeded) {
                const maxLimit = error.response?.data?.maxLimit || 120;
                iviewMessage.warning({
                    content: t('HTwbActKitHome.activityLimitExceeded', { maxLimit }),
                    duration: 6,
                    closable: true
                });

                return {
                    success: false,
                    error: 'ACTIVITY_LIMIT_EXCEEDED',
                    maxLimit
                };
            }

            // 其他錯誤：先顯示錯誤訊息
            iviewMessage.error({
                content: t('Game.pushFailed', { message: error.message }),
                duration: 5,
                closable: true
            });

            // 然後向 WebView 發送刷新請求
            iviewMessage.warning({
                content: t('Game.tokenRefreshing'),
                duration: 3,
                closable: true
            });

            options.messageBridge.postMessage({
                action: 'Get.GameInitInfo'
            });

            // ✨ 返回失敗結果
            return {
                success: false,
                error: error.message
            };
        }
    };

    return {
        createAndPushShortUrl,
        shortURLHost,
        isLoggedIn
    };
};
